/**
 * CountryDirectoryPage — who speaks for each country.
 *
 * A country's national office used to be whichever record happened to match
 * the country string. Here a super admin sets it explicitly, sees the
 * assembly count behind each country, and spots countries with no office.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe2, AlertTriangle, Building2, Wand2, X, Search } from 'lucide-react';
import { toast } from 'sonner';

import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

const Stat = ({ label, value, tone = 'slate', testId }) => (
  <div className={`rounded-xl border border-${tone}-200 bg-${tone}-50/60 px-5 py-4`}
       data-testid={testId}>
    <div className="text-2xl font-semibold text-slate-900">{value}</div>
    <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mt-1">{label}</div>
  </div>
);

const OfficeCell = ({ row, onAssign, onClear, saving }) => {
  const office = row.national_office;
  return (
    <div className="space-y-2">
      <Select
        value={office?.id || ''}
        onValueChange={(v) => onAssign(row.country, v)}
        disabled={saving || !row.candidates.length}
      >
        <SelectTrigger className="w-full max-w-sm"
                       data-testid={`office-select-${row.country}`}>
          <SelectValue placeholder={row.candidates.length
            ? 'Choose a national office'
            : 'No eligible record in this country'} />
        </SelectTrigger>
        <SelectContent>
          {row.candidates.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name}{c.city ? ` · ${c.city}` : ''} · {c.level || 'record'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex items-center gap-2 flex-wrap">
        {row.office_source === 'assigned' && (
          <>
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                   data-testid={`office-badge-${row.country}`}>Assigned</Badge>
            {office?.assigned_by_name && (
              <span className="text-xs text-slate-500">
                by {office.assigned_by_name}
                {office.assigned_at ? ` · ${new Date(office.assigned_at).toLocaleDateString()}` : ''}
              </span>
            )}
            <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500"
                    onClick={() => onClear(row.country)} disabled={saving}
                    data-testid={`office-clear-${row.country}`}>
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </>
        )}
        {row.is_inferred && (
          <Badge variant="outline" className="border-amber-300 text-amber-700"
                 data-testid={`office-badge-${row.country}`}>
            <Wand2 className="h-3 w-3 mr-1" /> Inferred — not yet assigned
          </Badge>
        )}
        {row.needs_office && (
          <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100"
                 data-testid={`office-badge-${row.country}`}>
            <AlertTriangle className="h-3 w-3 mr-1" /> No national office
          </Badge>
        )}
      </div>
    </div>
  );
};

export default function CountryDirectoryPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['country-directory'],
    queryFn: () => api.get('/admin/country-directory').then((r) => r.data),
  });

  const assign = useMutation({
    mutationFn: ({ country, church_id }) =>
      api.put(`/admin/country-directory/${encodeURIComponent(country)}`, { church_id }),
    onSuccess: (res, vars) => {
      toast.success(`${res.data.national_office.name} is now the national office for ${vars.country}`);
      qc.invalidateQueries({ queryKey: ['country-directory'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not assign that office'),
  });

  const clear = useMutation({
    mutationFn: (country) =>
      api.delete(`/admin/country-directory/${encodeURIComponent(country)}`),
    onSuccess: (_res, country) => {
      toast.success(`Cleared the national office for ${country}`);
      qc.invalidateQueries({ queryKey: ['country-directory'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not clear that assignment'),
  });

  const adopt = useMutation({
    mutationFn: () => api.post('/admin/country-directory/adopt-inferred'),
    onSuccess: (res) => {
      const n = res.data.adopted;
      toast.success(n
        ? `${n} inferred office${n === 1 ? '' : 's'} is now a real assignment`
        : 'Nothing left to adopt — every office is already assigned');
      qc.invalidateQueries({ queryKey: ['country-directory'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not adopt those offices'),
  });

  const rows = useMemo(() => {
    let list = data?.countries || [];
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((r) => r.country.toLowerCase().includes(needle));
    }
    if (onlyMissing) list = list.filter((r) => r.needs_office || r.is_inferred);
    return list;
  }, [data, q, onlyMissing]);

  const saving = assign.isPending || clear.isPending || adopt.isPending;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="country-directory-page">
      <div>
        <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-3">
          <Globe2 className="h-7 w-7 text-cyan-600" /> Country Directory
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Set the national office for each country. Until one is assigned the directory falls back to
          the only country-level record — shown here as <em>inferred</em>, not a real assignment.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Countries" value={data?.total_countries ?? 0} testId="stat-countries" />
          <Stat label="Assigned" value={data?.assigned ?? 0} tone="emerald" testId="stat-assigned" />
          <Stat label="Inferred" value={data?.inferred ?? 0} tone="amber" testId="stat-inferred" />
          <Stat label="No office" value={data?.missing ?? 0} tone="rose" testId="stat-missing" />
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <CardTitle className="text-base">Countries</CardTitle>
          <div className="flex items-center gap-2">
            {(data?.inferred ?? 0) > 0 && (
              <Button size="sm" onClick={() => adopt.mutate()} disabled={adopt.isPending}
                      data-testid="adopt-inferred-btn">
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                Adopt {data.inferred} inferred
              </Button>
            )}
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Find a country" className="pl-8 w-56"
                     data-testid="country-search" />
            </div>
            <Button variant={onlyMissing ? 'default' : 'outline'} size="sm"
                    onClick={() => setOnlyMissing((v) => !v)}
                    data-testid="filter-needs-attention">
              Needs attention
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : !rows.length ? (
            <p className="p-6 text-sm text-slate-500" data-testid="country-directory-empty">
              No countries match that filter.
            </p>
          ) : (
            <div className="divide-y">
              {rows.map((row) => (
                <div key={row.country}
                     className="grid md:grid-cols-[1fr_auto_2fr] gap-4 items-start px-6 py-5"
                     data-testid={`country-row-${row.country}`}>
                  <div>
                    <div className="font-medium text-slate-900">{row.country}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {row.record_count} record{row.record_count === 1 ? '' : 's'} in the directory
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1.5 text-slate-700"
                          data-testid={`assembly-count-${row.country}`}>
                      <Building2 className="h-4 w-4 text-slate-400" />
                      {row.assembly_count} assembl{row.assembly_count === 1 ? 'y' : 'ies'}
                    </span>
                    <span className="text-slate-400 text-xs">
                      {row.congregation_count} congregation{row.congregation_count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <OfficeCell row={row} saving={saving}
                              onAssign={(country, church_id) => assign.mutate({ country, church_id })}
                              onClear={(country) => clear.mutate(country)} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
